import { useEffect, useState } from "react";
import { init, createElement } from "@airwallex/components-sdk";
import { Button } from "@/components/ui/button";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { AirWallexUtils } from "@/utils/ach/airWallex";
import {
  BeneficiaryDocument,
  useAwxAuthorizeBeneficiaryAccountMutation,
  useUpsertAwxBeneficiaryAccountMutation,
} from "@common/utils/graphql";
import { env, isDevelopment } from "@common/utils";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { CaseConverter } from "@/utils/case-converter/case-converter";
import { beneficiaryValidator } from "./BankAccountDetails.model";
import { Body } from "@hypercard/web-components";
import posthog from "posthog-js";
import { useApolloClient } from "@apollo/client";
import { useToast } from "@/components/ui/use-toast";
import { usePreventSheetClose } from "@/components/ui/sheet";

interface Props {
  onClose: () => void;
}

export const BankAccountDetailsForm = ({ onClose }: Props) => {
  const { toast } = useToast();
  const client = useApolloClient();
  const [isUSAccount, setIsUSAcount] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [ready, setReady] = useState(false);
  const [formElement, setFormElement] = useState<any>(null);
  const [codeVerifier, setCodeVerifier] = useState<string>("");
  const [initializingSDK, setInitializingSDK] = useState<boolean>(false);
  const userStore = useUserStore.getState();

  // TODO: If you notice that we are using this in multiple places, switch to using a singleton
  useEffect(() => {
    posthog.stopSessionRecording();
    return () => {
      posthog.startSessionRecording();
    };
  }, []);

  usePreventSheetClose(() => true);

  const [
    authorizeAccount,
    { data: authorizationData, loading: authorizationLoading },
  ] = useAwxAuthorizeBeneficiaryAccountMutation();

  const [createBeneficiaryAccount, { data: beneficiaryAccountData, loading }] =
    useUpsertAwxBeneficiaryAccountMutation();

  useEffect(() => {
    const handleAuthFlow = async () => {
      const verifier = AirWallexUtils.generateCodeVerifier();
      const challenge =
        await AirWallexUtils.generateCodeChallengeFromVerifier(verifier);
      setCodeVerifier(verifier);

      authorizeAccount({
        variables: {
          codeChallenge: challenge,
        },
      });
    };
    handleAuthFlow();
  }, [authorizeAccount]);

  const handleSubmit = async () => {
    if (ready && formElement) {
      const formResult = await formElement.submit();
      const inputValues = new CaseConverter().toCamel(formResult.values) as any;

      if (
        !inputValues.beneficiary.firstName ||
        !inputValues.beneficiary.lastName
      ) {
        // Default the firstName and lastName based on userStore info if not provided
        inputValues.beneficiary.firstName = userStore.firstName || "";
        inputValues.beneficiary.lastName = userStore.lastName || "";
      }
      const parsedValues = beneficiaryValidator.safeParse(inputValues);
      if (parsedValues.success) {
        createBeneficiaryAccount({
          variables: {
            input: {
              ...parsedValues.data,
            },
          },
        });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to save bank accout, please try again",
        });
      }
    }
  };

  useEffect(() => {
    if (
      beneficiaryAccountData?.upsertAwxBeneficiaryAccount.__typename ===
      "MutationUpsertAwxBeneficiaryAccountSuccess"
    ) {
      const { beneficiary, nickname, beneficiaryId } =
        beneficiaryAccountData.upsertAwxBeneficiaryAccount.data;

      userStore.setIsBankAccountLinked(!!beneficiaryId);

      client.writeQuery({
        query: BeneficiaryDocument,
        data: {
          beneficiary: {
            __typename: "QueryBeneficiarySuccess",
            data: {
              __typename: "Beneficiary",
              nickname,
              beneficiary,
            },
          },
        },
      });
      onClose();
    }
  }, [client, beneficiaryAccountData, onClose, userStore]);

  useEffect(() => {
    if (
      authorizationData?.awxAuthorizeBeneficiaryAccount.__typename ===
        "MutationAwxAuthorizeBeneficiaryAccountSuccess" &&
      showForm
    ) {
      setInitializingSDK(true);
      const authCode =
        authorizationData.awxAuthorizeBeneficiaryAccount.data.authorizationCode;

      const initializeSDK = async () => {
        try {
          await init({
            authCode,
            codeVerifier: codeVerifier,
            env: isDevelopment ? "demo" : "prod",
            clientId: env.airWallexClientId,
          });

          const element = await createElement("beneficiaryForm", {
            defaultValues: {
              beneficiary: {
                additional_info: {
                  personal_email: userStore.personalEmail || "",
                },
                first_name: userStore.firstName || "",
                last_name: userStore.lastName || "",
                address: isUSAccount
                  ? {
                      city: userStore.residentialAddress?.city || "",
                      country_code: "US",
                      state: `US-${userStore.residentialAddress?.state}` || "",
                      street_address: userStore.residentialAddress?.line1 || "",
                      postcode: userStore.residentialAddress?.postalCode || "",
                    }
                  : undefined,
                entity_type: "PERSONAL",
                bank_details: isUSAccount
                  ? {
                      account_currency: "USD",
                      bank_country_code: "US",
                      local_clearing_system: "ACH",
                    }
                  : undefined,
              },
              // payment_methods: ["LOCAL"],
              payment_method: "LOCAL",
            },
            customizations: {
              fields: {
                "beneficiary.entity_type": {
                  hidden: true,
                },
                "beneficiary.bank_details.local_clearing_system": isUSAccount
                  ? {
                      hidden: true,
                    }
                  : {},
                "beneficiary.transfer_method": {
                  hidden: true,
                },
              },
            },
          });
          element.mount("#container");

          setFormElement(element);

          element.on("ready", () => {
            setReady(true);
          });

          /*
           * Additional Handlers
           * element.on("success", (data) => {});
           * element.on("change", ({ value }) => {});
           * element.on("error", ({ value }) => {});
           * element.on("formState", ({ loading, validating, errors }) => {});
           */
        } catch (error) {
          console.error("Error initializing Airwallex SDK:", error);
        } finally {
          setInitializingSDK(false);
        }
      };

      initializeSDK();
    } else {
      console.warn("Authorization Data not ready:", authorizationData);
    }
  }, [
    showForm,
    authorizationData,
    codeVerifier,
    userStore.firstName,
    userStore.lastName,
    userStore.personalEmail,
    userStore.residentialAddress?.line1,
    userStore.residentialAddress?.city,
    userStore.residentialAddress?.state,
    userStore.residentialAddress?.postalCode,
    isUSAccount,
  ]);

  const submitPreform = (isUS: boolean) => () => {
    setIsUSAcount(isUS);
    setShowForm(true);
  };

  const isLoading = loading || authorizationLoading || initializingSDK;

  if (isLoading) {
    return <FullPageLoader className="h-[80vh]" />;
  }

  if (showForm && ready) {
    return (
      <Button
        type="button"
        onClick={handleSubmit}
        className="w-full"
        id="submit-button"
      >
        Submit
      </Button>
    );
  }

  if (!showForm) {
    return (
      <>
        <Body size="md" weight="regular">
          Is your bank account in the United States?
        </Body>
        <div className="mt-4 flex w-full">
          <Button className="mr-4 flex grow" onClick={submitPreform(true)}>
            Yes
          </Button>
          <Button
            variant="secondary"
            className="flex grow"
            onClick={submitPreform(false)}
          >
            No
          </Button>
        </div>
      </>
    );
  }
};
